import { Injectable, Injector } from "@angular/core";
import {
  HttpHeaders,
} from "@angular/common/http";

import * as Sentry from "@sentry/angular";
import { saveAs } from "file-saver";
import { ToastrService } from "ngx-toastr";

import { ApplicationApiService } from "./application-api.service";
import { ModalPDFViewerService } from "src/app/shared/components/pdf-viewer/pdf-viewer.service";
import { NgxSpinnerService } from "ngx-spinner"; // Assuming you have this or similar

function SafelyParseJSON(json) {
  try {
    return JSON.parse(json);
  } catch (e) {
    return;
  }
}

export interface SendTo {
  emails?: string[];
  waNumbers?: string[];
  text?: string;
}

@Injectable({ providedIn: 'root' })
export class PDFService extends ApplicationApiService {
  private isModalOpen: boolean = false;
  private modalQueue: Array<() => void> = [];

  constructor(
    protected injector: Injector,
    private pdfViewerService: ModalPDFViewerService,
    private spinnerService: NgxSpinnerService, // Inject a loading spinner service
    private toastr: ToastrService,
  ) {
    super(injector);
  }

  public get(
    url: string,
    search?: { [key: string]: any | any[] },
    fileName: string = "relatorio",
    sendTo: SendTo = null
  ): Promise<any> {
    const rep_type = (search ? search["rep_type"] : "pdf") || "pdf";
    // return this.request('GET', url, { params: this.createHttpParams(search), rep_type }, fileName);
    return this.getReport(
      "GET",
      url,
      { params: this.createHttpParams(search), rep_type, sendTo },
      fileName
    );
  }

  public post(
    url: string,
    body: any,
    fileName: string = "relatorio",
    sendTo: SendTo = null
  ): Promise<any> {
    // return this.request('POST', url, { body: JSON.stringify(body) }, fileName);
    return this.getReport(
      "POST",
      url,
      { body: JSON.stringify(body), sendTo },
      fileName
    );
  }

  private getReport(
    method: string,
    url: string,
    options: any,
    fileName: string = "relatorio"
  ): Promise<any> {
    const repType = (options ? options["rep_type"] : "pdf") || "pdf";
    const repName = fileName.endsWith(repType) ? fileName : `${fileName}.${repType}`;

    let waNumbers: string[];
    let waText: string = null;
    const sendTo = options ? options["sendTo"] : null;
    if (sendTo) {
      // Verifica se há números de WhatsApp para envio do arquivo
      waNumbers = sendTo["waNumbers"] || [];

      // Verifica se há texto para enviar
      waText = sendTo["text"];
    }

    // Show loading spinner using a service
    this.spinnerService.show()

    options["responseType"] = "blob";
    options["headers"] = new HttpHeaders({
      Accept: `application/${repType}, application/json`,
      "content-type":
        method === "POST" ? "application/json" : `application/${repType}`,
    });

    return new Promise<any>((resolve, reject) => {
      const subscription = this.http
        .request(method, url, options)
        .pipe()
        .subscribe(
          (resp) => {
            try {
              // Hide spinner
              this.spinnerService.hide();

              const blobObj = new Blob([resp], { type: "application/pdf" });

              if (repType !== "pdf") {
                const blobURL = URL.createObjectURL(blobObj);
                saveAs(blobURL, repName);
                // Limpar URL após uso
                setTimeout(() => URL.revokeObjectURL(blobURL), 1000);
              } else {
                // Usar sistema de fila para evitar aberturas múltiplas
                this.queuePdfModal(() => {
                  // Verificar se o serviço ainda está disponível
                  if (this.pdfViewerService) {
                    this.pdfViewerService.show({
                      src: blobObj,
                      filename: repName,
                      title: fileName,
                      waNumbers,
                      waText,
                    }).then(() => {
                      // Modal foi fechado, processar próximo na fila
                      this.processNextInQueue();
                    }).catch(() => {
                      // Erro ao abrir modal, processar próximo na fila
                      this.processNextInQueue();
                    });
                  }
                });
              }

              resolve({ ok: true });
            } catch (e) {
              console.error("PDF Service Error:", e);
              this.spinnerService.hide();
              resolve({ ok: false, error: e.message });
            } finally {
              // Limpar subscription
              if (subscription) {
                subscription.unsubscribe();
              }
            }
          },
          async (err) => {
            this.spinnerService.hide();

            // Lê o corpo da resposta (vem como Blob por causa do
            // responseType: "blob") para anexar ao Sentry e ao reject.
            let bodyText: string | null = null;
            let bodyParsed: any = null;
            try {
              if (err.error instanceof Blob) {
                bodyText = await err.error.text();
                bodyParsed = SafelyParseJSON(bodyText);
              }
            } catch {
              // Ignora falha de leitura — segue com bodyText/bodyParsed nulos.
            }

            // statusText "OK" em respostas de erro é o default do Angular
            // quando o backend não envia o reason phrase — ofusca o problema.
            const statusText =
              err.status >= 400 && err.statusText === "OK"
                ? null
                : err.statusText;

            // 4xx são erros de validação/negócio (usuário já vê a mensagem
            // na tela) — não acionáveis e geram ruído no Sentry. Só
            // reportamos rede (0) e erros reais de servidor (>=500).
            const isReportable = err.status === 0 || err.status >= 500;

            if (isReportable) {
              Sentry.withScope((scope) => {
                scope.setContext("http_error", {
                  status: err.status,
                  statusText,
                  url: err.url,
                  message: err.message,
                  body: bodyParsed ?? bodyText,
                });
                Sentry.captureMessage(
                  `PDF download falhou: HTTP ${err.status}`,
                  "warning"
                );
              });
            }

            // Exibe toast com a mensagem do backend para o usuário.
            // A maioria dos chamadores faz fire-and-forget, então o
            // feedback precisa vir daqui. Resolve (não rejeita) para
            // evitar "Uncaught (in promise)" capturado pelo Angular.
            // Backend retorna mensagens em vários formatos: array direto
            // (["Informe uma pessoa!"]), {error:[...]}, {error:{message}},
            // ou {message}. Cobrimos todos.
            const userMessage =
              (Array.isArray(bodyParsed) && bodyParsed.join(', ')) ||
              (Array.isArray(bodyParsed?.error) && bodyParsed.error.join(', ')) ||
              bodyParsed?.error?.message ||
              bodyParsed?.message ||
              bodyText ||
              err.message ||
              `Falha no download (HTTP ${err.status}).`;
            this.toastr.error(userMessage, 'Falha ao gerar relatório');

            resolve({
              ok: false,
              error: bodyParsed ?? { message: err.message },
            });

            if (subscription) {
              subscription.unsubscribe();
            }
          }
        );
    });
  }

  /**
   * Adiciona uma operação de abertura de PDF à fila
   * Previne race conditions quando múltiplos PDFs são abertos rapidamente
   */
  private queuePdfModal(openModalFn: () => void): void {
    if (!this.isModalOpen) {
      // Nenhum modal aberto, abrir imediatamente
      this.isModalOpen = true;
      openModalFn();
    } else {
      // Modal já está aberto, adicionar à fila
      console.log('PDF modal already open, queueing request...');
      this.modalQueue.push(openModalFn);
    }
  }

  /**
   * Processa o próximo item na fila de modais
   */
  private processNextInQueue(): void {
    // Aguardar um pouco antes de processar o próximo para garantir cleanup
    setTimeout(() => {
      this.isModalOpen = false;

      if (this.modalQueue.length > 0) {
        const nextModal = this.modalQueue.shift();
        if (nextModal) {
          console.log(`Processing queued PDF modal (${this.modalQueue.length} remaining in queue)`);
          this.isModalOpen = true;
          nextModal();
        }
      }
    }, 150); // Delay para garantir que o modal anterior foi totalmente destruído
  }
}
